
  import { Vue, Component, Prop } from "vue-property-decorator";
  import UiButton from "@/components/button/ui-button.vue";
  import ApplicationNoteModal from "@/components/jobboard/ApplicationNoteModal.vue";
  import ItemChipsData from "@/entities/ItemChipsData";
  import FormTextarea from "@/components/form/FormTextarea.vue";
  import FormGroup from "@/components/form/FormGroup.vue";
  import UploadedFileData from "@/entities/file/UploadedFileData";
  import INSERT_APPLICATION_DOCUMENT from "@/apollo/applications/InsertApplicationDocument.gql";
  import UPDATE_APPLICATION from "@/apollo/applications/UpdateApplication.gql";
  import { NOTIFICATION } from "@/entities/Events";
  import { defaultErrorNotificationMessage } from "@/data/errorMessages";
  import Message from '@/data/notificationsMessages';

  @Component({
    components: {
      FormGroup,
      FormTextarea,
      ApplicationNoteModal,
      UiButton,
    },
  })
  export default class ApplicationNote extends Vue {
    @Prop({ type: Array, required: true }) readonly skills!: ItemChipsData[];
    @Prop({ type: Array, required: true }) readonly industries!: ItemChipsData[];
    @Prop({ type: String, required: true }) readonly currentUserId!: string;
    @Prop({ type: String, required: true }) readonly applicationId!: string;
    @Prop({ type: String, required: true }) readonly postingId!: string;
    @Prop({ type: String, required: true }) readonly jobTitle!: string;
    @Prop({ type: String, required: true }) readonly companyName!: string;
    @Prop({ type: String, required: true }) readonly employmentType!: string;
    @Prop({ type: String, required: true }) readonly postedDate!: string;
    @Prop({ type: Number, default: 0 }) readonly duration!: number;
    @Prop({ type: String, required: undefined }) readonly location?: string;
    @Prop({ type: String, default: undefined }) readonly description?: string;
    @Prop({ type: String, default: "" }) readonly note!: string;
    @Prop({ type: String, default: undefined }) readonly buttonVariant!: string;
    @Prop({ type: String, default: undefined }) readonly attachedDocumentId?: string;

    modalShown: boolean = false;
    applicationNote: string = this.note;
    processing: boolean = false;

    get actionButtonText(): string {
      if (this.note) {
        return "Update";
      }

      return "Add";
    }

    get noteButtonText(): string {
      if (this.note) {
        return "Update Comments";
      }

      return "Add a Comment";
    }

    async onUpdate(selectedDocumentId: string | undefined): Promise<void> {
      this.processing = true;

      try {
        await this.$apollo.mutate({
          mutation: UPDATE_APPLICATION,
          variables: {
            applicationId: this.applicationId,
            note: this.applicationNote,
            documentId: selectedDocumentId || null,
          },
        });

        this.showSuccessMessage();
        this.modalShown = false;
        this.$emit("applicationNoteUpdated");
      } catch (_) {
        return this.$eventBus.emit(NOTIFICATION, Message.errorMessage(defaultErrorNotificationMessage));
      } finally {
        this.processing = false;
      }
    }

    async onAttachNewFile(fileData: UploadedFileData): Promise<void> {
      this.processing = true;

      try {
        await this.$apollo.mutate({
          mutation: INSERT_APPLICATION_DOCUMENT,
          variables: {
            userId: this.currentUserId,
            postingId: this.postingId,
            applicationId: this.applicationId,
            note: this.applicationNote,
            document: fileData,
          },
        });
        this.showSuccessMessage();
        this.modalShown = false;
        this.$emit("applicationNoteUpdated");
      } catch (_) {
        return this.$eventBus.emit(NOTIFICATION, Message.errorMessage(defaultErrorNotificationMessage));
      } finally {
        this.processing = false;
      }
    }

    showSuccessMessage(): void {
      if (this.note) {
        this.$eventBus.emit(NOTIFICATION, Message.successMessage("Application comment updated"));
        return;
      }

      this.$eventBus.emit(NOTIFICATION, Message.successMessage("Application comment added"));
    }

    onModalClose(): void {
      this.modalShown = false;
      this.applicationNote = this.note;
    }
  }
